import React, { useEffect, useState, useMemo } from "react";
import Menu from "../panel/Menu";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import {
  Visibility,
  HotelOutlined,
  AssignmentTurnedIn,
  CheckCircleOutline
} from "@mui/icons-material";

import {
  doc, getDoc, collection, getDocs, query, where,
  Timestamp, addDoc, getCountFromServer, updateDoc, setDoc, serverTimestamp
} from "firebase/firestore";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, Paper, IconButton, Button
} from "@mui/material";
import Swal from 'sweetalert2';

const NAVY = "#0a2540";
const NAVY_LIGHT = "#1e3a8a";
const GOLD = "#d4af37";
const GRADIENT_PRIMARY = "linear-gradient(135deg, #0a2540 0%, #1a365d 100%)";

const REPORTS = [
  { title: "DCR Report", link: "/dcr-report" },
  { title: "Datewise Bookings", link: "/datewise-bookings" },
  { title: "Receptionist Report", link: "/receptionist-report" },
  { title: "Paymodewise Report", link: "/paymodewise-report" },
  { title: "Roomtypewise Report", link: "/roomtypewise-report" },
  { title: "Booking Type Wise Report", link: "/booking-typewise-report" },
];

function Dashboard() {
  const Toast = useMemo(() => Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  }), []);

  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [propertyId, setPropertyId] = useState(null);
  const [propertyStatus, setPropertyStatus] = useState(null);
  const [propertyName, setPropertyName] = useState("");
  const [dynamicBarData, setDynamicBarData] = useState([]);
  const [dynamicPieData, setDynamicPieData] = useState([]);
  const [hotelCode, setHotelCode] = useState("");
  const [summary, setSummary] = useState({
    today: 0, tomorrow: 0, month: 0, future: 0, live: 0, available: 0, cleaning: 0, totalRooms: 0,
    whatsappCurrentMonth: 0, whatsappPrevMonth: 0
  });

  // Auth listener
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => setUserData(user));
    return () => unsub();
  }, []);

  // Combined Dashboard & Property Initializer
  useEffect(() => {
    if (!userData) return;
    const loadDashboardData = async () => {
      try {
        const roleSnap = await getDoc(doc(db, "authroles", userData.uid));
        if (!roleSnap.exists() || !roleSnap.data().propertyId) {
          setPropertyStatus("no-property");
          setLoading(false);
          return;
        }

        const activePropertyId = roleSnap.data().propertyId;
        setPropertyId(activePropertyId);

        const [propSnap, cmSnap] = await Promise.all([
          getDoc(doc(db, "properties", activePropertyId)),
          getDoc(doc(db, "channel_manager", activePropertyId))
        ]);

        if (propSnap.exists()) {
          const p = propSnap.data();
          setPropertyName(p.hotelname || "Premier Property");
          setPropertyStatus(p.status || "Active");
          if (p.status === "Active") {
            await fetchDashboardAnalytics(activePropertyId);
          }
        }

        if (cmSnap.exists()) {
          const cmData = cmSnap.data();
          setHotelCode(cmData.hotelCode || "");
        }
      } catch (err) {
        console.error("Dashboard Engine Load Error: ", err);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [userData]);

  const fetchDashboardAnalytics = async (targetPropertyId) => {
    const todayStr = moment().format("YYYY-MM-DD");
    const tomorrowStr = moment().add(1, "day").format("YYYY-MM-DD");
    const monthStartStr = moment().startOf("month").format("YYYY-MM-DD");
    const last7DaysStr = moment().subtract(6, "days").format("YYYY-MM-DD");

    // WhatsApp Timeline Constraints
    const currentMonthStart = Timestamp.fromDate(moment().startOf("month").toDate());
    const currentMonthEnd = Timestamp.fromDate(moment().endOf("month").toDate());
    const prevMonthStart = Timestamp.fromDate(moment().subtract(1, "month").startOf("month").toDate());
    const prevMonthEnd = Timestamp.fromDate(moment().subtract(1, "month").endOf("month").toDate());

    const waCurrentQuery = query(
      collection(db, "whatsapphistory"),
      where("propertyId", "==", targetPropertyId),
      where("createdAt", ">=", currentMonthStart),
      where("createdAt", "<=", currentMonthEnd)
    );

    const waPrevQuery = query(
      collection(db, "whatsapphistory"),
      where("propertyId", "==", targetPropertyId),
      where("createdAt", ">=", prevMonthStart),
      where("createdAt", "<=", prevMonthEnd)
    );

    const [
      todaySnap, tomorrowSnap, monthSnap, futureSnap,
      allResSnap, roomsSnap, waCurrentSnap, waPrevSnap
    ] = await Promise.all([
      getDocs(query(collection(db, "onlinebookings"), where("propertyId", "==", targetPropertyId), where("status", "==", "Paid"), where("checkindate", "==", todayStr))),
      getDocs(query(collection(db, "onlinebookings"), where("propertyId", "==", targetPropertyId), where("status", "==", "Paid"), where("checkindate", "==", tomorrowStr))),
      getDocs(query(collection(db, "onlinebookings"), where("propertyId", "==", targetPropertyId), where("status", "==", "Paid"), where("checkindate", ">=", monthStartStr))),
      getDocs(query(collection(db, "onlinebookings"), where("propertyId", "==", targetPropertyId), where("status", "==", "Paid"), where("checkindate", ">", tomorrowStr))),
      getDocs(query(collection(db, "reservations"), where("propertyId", "==", targetPropertyId), where("checkindate", ">=", last7DaysStr))),
      getDocs(query(collection(db, "roominfos"), where("propertyId", "==", targetPropertyId))),
      getCountFromServer(waCurrentQuery),
      getCountFromServer(waPrevQuery)
    ]);

    const rooms = roomsSnap.docs.map(d => d.data());
    const available = rooms.filter(r => r.status?.toLowerCase() === "available").length;
    const cleaning = rooms.filter(r => r.status?.toLowerCase() === "cleaning").length;
    const occupied = rooms.filter(r => ["booked", "occupied", "live"].includes(r.status?.toLowerCase())).length;

    setSummary({
      today: todaySnap.size,
      tomorrow: tomorrowSnap.size,
      month: monthSnap.size,
      future: futureSnap.size,
      live: occupied,
      available,
      cleaning,
      totalRooms: roomsSnap.size || rooms.length,
      whatsappCurrentMonth: waCurrentSnap.data().count,
      whatsappPrevMonth: waPrevSnap.data().count
    });

    const timeSeriesMap = {};
    for (let i = 6; i >= 0; i--) {
      const dayLabel = moment().subtract(i, "days").format("DD MMM");
      timeSeriesMap[dayLabel] = 0;
    }

    allResSnap.forEach(docItem => {
      const data = docItem.data();
      if (data.checkindate) {
        const formattedDate = moment(data.checkindate).format("DD MMM");
        if (timeSeriesMap[formattedDate] !== undefined) timeSeriesMap[formattedDate]++;
      }
    });

    setDynamicBarData(Object.entries(timeSeriesMap).map(([date, bookings]) => ({ date, bookings })));
    setDynamicPieData([
      { name: "Available", value: available, color: "#10b981" },
      { name: "Cleaning", value: cleaning, color: "#64748b" },
      { name: "Occupied", value: occupied, color: "#ef4444" }
    ]);
  };

  const processReservationTransfer = async (booking, isAuto = false) => {
    if (!propertyId) return;

    try {
      if (!isAuto) Toast.fire({ icon: "info", title: "Processing reservation transfer..." });

      const resReservation = booking.payload?.HotelReservations?.HotelReservation;
      const otaId = resReservation?.UniqueID?.ID || `ota-${booking.id}`;

      // 1. Prevent Duplicate Processing Early
      const otaLogRef = doc(db, "properties", propertyId, "ota_raw_logs", String(otaId));
      const logSnap = await getDoc(otaLogRef);

      if (logSnap.exists() && logSnap.data().syncStatus === "transferred") {
        if (!isAuto) Toast.fire({ icon: "warning", title: "Already transferred!" });
        return;
      }

      // 2. Extract Data Safely
      const customer = resReservation?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
      const roomStay = resReservation?.RoomStays?.[0];
      const channelName = booking.payload?.POS?.Source?.BookingChannel?.CompanyName || "OTA Channel";

      let cmId = "";
      let roomNameOTA = "Unassigned OTA Room";
      const rtNode = roomStay?.RoomTypes?.RoomType;

      if (Array.isArray(rtNode)) {
        cmId = rtNode[0]?.RoomTypeCode || rtNode[0]?.RoomID || "";
        roomNameOTA = rtNode[0]?.RoomDescription?.Name || roomNameOTA;
      } else if (rtNode) {
        cmId = rtNode?.RoomTypeCode || rtNode?.RoomID || "";
        roomNameOTA = rtNode?.RoomDescription?.Name || roomNameOTA;
      }

      const custname = `${customer?.PersonName?.GivenName || ""} ${customer?.PersonName?.Surname || ""}`.trim() || "Guest";
      const email = customer?.Email || "N/A";
      const mobile = customer?.Telephone?.[0]?.PhoneNumber || "0000000000";

      const checkindate = roomStay?.TimeSpan?.Start || moment().format("YYYY-MM-DD");
      const checkoutdate = roomStay?.TimeSpan?.End || moment().add(1, "days").format("YYYY-MM-DD");

      const totalamount = Number(roomStay?.Total?.AmountAfterTax || roomStay?.Total?.AmountBeforeTax || 0);

      const inMoment = moment(checkindate);
      const outMoment = moment(checkoutdate);
      const noofdays = outMoment.diff(inMoment, 'days') > 0 ? outMoment.diff(inMoment, 'days') : 1;

      const roomRate = totalamount / noofdays;

      // 3. Room Assignment Strategy
      let targetRoomTypeName = roomNameOTA;

      if (cmId) {
        const qRoomTypes = query(collection(db, "rooms"), where("propertyId", "==", propertyId));
        const roomTypesSnap = await getDocs(qRoomTypes);
        const allRoomTypes = roomTypesSnap.docs.map(d => d.data());

        const matchedType = allRoomTypes.find(rt => String(rt.cmId).trim() === String(cmId).trim());
        if (matchedType && matchedType.roomTypeName) {
          targetRoomTypeName = matchedType.roomTypeName;
        }
      }

      const qRooms = query(collection(db, "roominfos"), where("propertyId", "==", propertyId));
      const availableRoomsSnap = await getDocs(qRooms);
      let assignedRoomData = null;

      if (!availableRoomsSnap.empty) {
        const allRooms = availableRoomsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        let matchingRooms = [];
        if (targetRoomTypeName && targetRoomTypeName !== "Unassigned OTA Room") {
          matchingRooms = allRooms.filter(r => String(r.roomTypeName).trim() === String(targetRoomTypeName).trim());
        }

        if (matchingRooms.length > 0) {
          const availableMatching = matchingRooms.filter(r => r.status === "Available" || !r.status);
          assignedRoomData = availableMatching.length > 0 ? availableMatching[0] : matchingRooms[0];

          if (assignedRoomData && availableMatching.length > 0) {
            await updateDoc(doc(db, "roominfos", assignedRoomData.id), { status: "Occupied" });
          }
        }
      }

      const bookingrooms = [{
        roomid: assignedRoomData ? assignedRoomData.id : `pending_ota_${Date.now()}`,
        rtypeid: assignedRoomData ? assignedRoomData.roomTypeId : "ota_pending_type",
        planid: "ota_plan",
        roomno: assignedRoomData ? assignedRoomData.roomNo : `OTA: ${roomNameOTA}`,
        planname: "EP",
        roomtypename: assignedRoomData ? assignedRoomData.roomTypeName : targetRoomTypeName,
        roomrate: roomRate,
        discount: 0,
        amount: totalamount
      }];

      // 4. Update Inventory locally in 'ota_room_inventory' based on cmId
      if (cmId) {
        const otaInvRef = doc(db, "ota_room_inventory", propertyId);
        const otaInvSnap = await getDoc(otaInvRef);

        if (otaInvSnap.exists()) {
          let otaRooms = otaInvSnap.data().rooms || [];
          let isUpdated = false;

          otaRooms = otaRooms.map(r => {
            if (String(r.RoomId).trim() === String(cmId).trim()) {
              isUpdated = true;
              if (r.AvailableCount !== undefined) {
                return { ...r, AvailableCount: Math.max(0, Number(r.AvailableCount) - 1) };
              }
              if (r.count !== undefined) {
                return { ...r, count: Math.max(0, Number(r.count) - 1) };
              }
            }
            return r;
          });

          if (isUpdated) {
            await updateDoc(otaInvRef, { rooms: otaRooms, lastSyncAt: serverTimestamp() });
          }
        }
      }

      // 5. Create Customer Record
      const customerData = {
        custname, mobile, email, address: "", city: "", state: "", gender: "", gstname: "", gstno: "", documenttype: "", otherDocumentType: "", idno: "",
        propertyId, uid: auth.currentUser?.uid || "system", createdAt: Timestamp.now(),
      };
      const customerRef = await addDoc(collection(db, "customers"), customerData);

      // 6. Generate Invoice Number
      const q = query(collection(db, "reservations"), where("propertyId", "==", propertyId));
      const bookingCountSnap = await getCountFromServer(q);
      const invoiceNo = bookingCountSnap.data().count + 1;

      // 7. Create Reservation
      const advance = totalamount;
      const remaining = 0;

      const bookingPayload = {
        customerId: customerRef.id, invoiceno: invoiceNo, customers: customerData, rooms: bookingrooms, services: [],
        checkindate, checkoutdate, noofdays, checkintime: "12:00", checkouttime: "11:00", adults: 2, childs: 0, noofrooms: 1,
        visitpurpose: "Leisure", bookingsource: channelName, otaId: otaId,
        visitorPhotoUrl: "", idFrontUrl: "", idBackUrl: "", couplePhotoUrl: "",
        rooamount: totalamount, roundoff: 0, totalamount: totalamount,
        advance: advance, remaining: remaining, note: `Transferred from ${channelName} (ID: ${otaId})`, bookingDate: moment().format("YYYY-MM-DD"), type: "Regular", status: "Booked", coming: "No",
        approval1: "No", approval2: "No", approval3: "No", approval4: "No", damageamount: 0,
        propertyId, uid: auth.currentUser?.uid || "system", additionalAdults: [], createdAt: Timestamp.now(),
      };
      const bookingRef = await addDoc(collection(db, "reservations"), bookingPayload);

      // 8. Generate Check-in Details across ALL days of stay (e.g., 05/09 to 07/09 generates records for 05/09 and 06/09)
      for (let day = 0; day < noofdays; day++) {
        const inDate = moment(checkindate).add(day, "days").format("YYYY-MM-DD");
        const outDate = moment(checkindate).add(day + 1, "days").format("YYYY-MM-DD");
        for (const room of bookingrooms) {
          await addDoc(collection(db, "checkindetails"), {
            bookingid: bookingRef.id, customerId: customerRef.id, checkindate: inDate, checkoutdate: outDate,
            propertyId, roomid: room.roomid, roomtypeid: room.rtypeid, planid: room.planid, roomname: room.roomno,
            planname: room.planname,
            roomtype: room.roomtypename || "OTA Category",
            status: "Booked", uid: auth.currentUser?.uid || "system", createdAt: Timestamp.now(),
          });
        }
      }

      // 9. Generate Payment History across all days
      if (advance > 0) {
        for (let i = 0; i < noofdays; i++) {
          await addDoc(collection(db, "payhistory"), {
            bookingid: bookingRef.id,
            customerId: customerRef.id,
            paymode1: "",
            amount1: advance / noofdays,
            paytime1: moment().format("HH:mm"),
            paymode2: "",
            amount2: 0,
            paytime2: "",
            type: "Booking",
            paymenttype: "B",
            status: "Working",
            propertyId,
            uid: auth.currentUser?.uid || "system",
            note: `${channelName} Pre-paid`,
            roomRate: roomRate,
            totalAmount: totalamount,
            createdAt: Timestamp.fromDate(new Date(new Date().setDate(new Date().getDate() + i))),
          });
        }
      }

      // --- 10. EXTERNAL OTA INVENTORY SYNC API INTEGRATION ---
      try {
        if (cmId) {
          const inventorySyncPayload = [];
          const otaSnap = await getDocs(query(collection(db, "ota_inventory_updates"), where("propertyId", "==", propertyId), where("status", "==", "Synced")));
          const otaUpdates = otaSnap.docs.map(d => d.data()).sort((a, b) => new Date(b.syncedAt || 0) - new Date(a.syncedAt || 0));

          const getOtaAvailability = (targetCmId, dateStr) => {
            const overlapping = otaUpdates.find(u => String(u.roomId) === String(targetCmId) && dateStr >= u.startDate && dateStr <= u.endDate);
            return overlapping ? Number(overlapping.availability) : 0;
          };

          for (let day = 0; day < noofdays; day++) {
            const dateStr = moment(checkindate).add(day, "days").format("YYYY-MM-DD");
            const currentAvail = getOtaAvailability(cmId, dateStr);

            if (currentAvail > 0) {
              const newAvail = Math.max(0, currentAvail - 1);
              inventorySyncPayload.push({
                roomId: String(cmId),
                roomName: assignedRoomData ? assignedRoomData.roomTypeName : roomNameOTA,
                startDate: dateStr,
                endDate: dateStr,
                availability: String(newAvail)
              });
            }
          }

          if (inventorySyncPayload.length > 0) {
            const configSnap = await getDoc(doc(db, "channel_manager", propertyId));
            if (configSnap.exists()) {
              const { hotelCode: configHotelCode, authCode } = configSnap.data();
              const payload = {
                Authentication: { HotelCode: configHotelCode, AuthCode: authCode },
                Inventories: {
                  Inventory: inventorySyncPayload.map(item => ({
                    RoomId: item.roomId,
                    FromDate: item.startDate,
                    ToDate: item.endDate,
                    AvailableRoomCount: item.availability
                  }))
                }
              };

              const response = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/inventory-update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });

              const result = await response.json();
              if (result.APIResponse?.IsSuccess) {
                const promises = inventorySyncPayload.map(item => addDoc(collection(db, "ota_inventory_updates"), {
                  propertyId,
                  roomId: item.roomId,
                  roomName: item.roomName,
                  availability: item.availability,
                  startDate: item.startDate,
                  endDate: item.endDate,
                  status: "Synced",
                  syncedAt: new Date().toISOString(),
                  createdAt: serverTimestamp()
                }));
                await Promise.all(promises);
              }
            }
          }
        }
      } catch (otaError) {
        console.error("OTA Sync Error during reservation transfer: ", otaError);
      }
      // --- END EXTERNAL OTA INVENTORY SYNC ---

      // 11. Update OTA log status to transferred
      await setDoc(otaLogRef, {
        otaLogId: booking.id,
        createdAt: booking.createdAt || new Date().toISOString(),
        syncStatus: "transferred",
        localBookingId: bookingRef.id,
        payload: booking.payload
      }, { merge: true });

      setBookings(prevBookings => prevBookings.map(b => {
        const bOtaId = b.payload?.HotelReservations?.HotelReservation?.UniqueID?.ID || `ota-${b.id}`;
        if (String(bOtaId) === String(otaId)) return { ...b, isTransferred: true };
        return b;
      }));

      if (!isAuto) Toast.fire({ icon: "success", title: "Reservation successfully transferred!" });
      if (!isAuto) fetchDashboardAnalytics(propertyId);

    } catch (error) {
      console.error("Transfer Error:", error);
      if (!isAuto) Toast.fire({ icon: "error", title: "Transfer failed", text: error.message });
    }
  };

  const autoSyncBookings = async (incomingBookings) => {
    if (!propertyId || incomingBookings.length === 0) return;
    try {
      let syncOccurred = false;
      for (const row of incomingBookings) {
        if (!row.isTransferred) {
          await processReservationTransfer(row, true);
          syncOccurred = true;
        }
      }
      if (syncOccurred) fetchDashboardAnalytics(propertyId);
    } catch (err) {
      console.error("Log auto-sync error:", err);
    }
  };

  const getChannelChip = (channel) => {
    const normalized = channel?.toLowerCase() || "";
    let logo = "/logos/booking.png";
    if (normalized.includes("mmt") || normalized.includes("makemytrip")) logo = "/logos/mmt.png";
    else if (normalized.includes("goibibo")) logo = "/logos/goibibo.png";
    else if (normalized.includes("agoda")) logo = "/logos/agoda.png";
    else if (normalized.includes("expedia")) logo = "/logos/expedia.jpg";
    return <img src={logo} alt={channel} style={{ width: 40, height: 40, objectFit: "contain" }} />;
  };

  const handleViewDetails = (booking) => {
    const resDetails = booking.payload?.HotelReservations?.HotelReservation;
    const customer = resDetails?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
    const roomStay = resDetails?.RoomStays?.[0];

    const email = customer?.Email || "N/A";
    const commentText = resDetails?.ResGlobalInfo?.Comments?.Comment?.Text || roomStay?.Comments?.Comment?.Text || "None";
    const payoutType = roomStay?.DepositPayments?.GuaranteePayment?.Description?.Text || "N/A";
    const guestCounts = roomStay?.GuestCounts || [];
    const structuredGuests = guestCounts.map(g => `${g.AgeQualifyingCode === "10" ? "Adults" : "Children"}: ${g.Count}`).join(', ') || "N/A";

    Swal.fire({
      title: `<strong style="color:${NAVY}">Booking Details (ID: ${resDetails?.UniqueID?.ID || booking.id})</strong>`,
      html: `
        <div style="text-align: left; font-size: 0.9rem; max-height: 480px; overflow-y: auto; font-family: sans-serif; color: #334155;">
            <div style="margin-bottom: 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">
                <p style="margin: 4px 0;"><strong>Guest Email:</strong> ${email}</p>
                <p style="margin: 4px 0;"><strong>Occupancy Breakdown:</strong> ${structuredGuests}</p>
                <p style="margin: 4px 0;"><strong>Payment Basis:</strong> ${payoutType}</p>
            </div>
            <p style="margin: 4px 0; font-weight: bold; color: #1e293b;">Special Requests &amp; Rates:</p>
            <pre style="background: #f1f5f9; padding: 12px; border-radius: 6px; white-space: pre-wrap; font-family: monospace; font-size: 0.8rem; border: 1px solid #e2e8f0; margin-top: 6px;">${commentText}</pre>
        </div>
      `,
      showCloseButton: true,
      confirmButtonColor: NAVY_LIGHT
    });
  };

  useEffect(() => {
    if (!hotelCode || !propertyId) return;

    const fetchOtaBookings = async () => {
      try {
        const transferredIds = new Set();
        const logsSnap = await getDocs(collection(db, "properties", propertyId, "ota_raw_logs"));
        logsSnap.forEach(d => {
          if (d.data().syncStatus === "transferred") {
            transferredIds.add(String(d.id));
          }
        });

        const response = await fetch('https://booking.thekingsexecutive.com/api/webhooks-data');
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          const todayStr = new Date().toISOString().split('T')[0];
          const uniqueBookingsMap = new Map();

          result.data.forEach(item => {
            let parsedInnerData = {};
            try {
              parsedInnerData = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
            } catch (e) {
              console.error("Parse error:", e);
            }

            const resReservation = parsedInnerData?.HotelReservations?.HotelReservation;
            const otaId = resReservation?.UniqueID?.ID;
            const checkInDate = resReservation?.RoomStays?.[0]?.TimeSpan?.Start;
            const incomingHotelCode = resReservation?.RoomStays?.[0]?.BasicPropertyInfo?.HotelCode;

            if (incomingHotelCode && String(incomingHotelCode).trim() === String(hotelCode).trim()) {
              if (checkInDate && checkInDate >= todayStr) {
                const isTransferred = otaId ? transferredIds.has(String(otaId)) : false;
                const formattedItem = { id: item.id, createdAt: item.created_at, payload: parsedInnerData, isTransferred };

                if (otaId) {
                  if (!uniqueBookingsMap.has(otaId) || uniqueBookingsMap.get(otaId).id < item.id) {
                    uniqueBookingsMap.set(otaId, formattedItem);
                  }
                } else {
                  uniqueBookingsMap.set(`fallback-${item.id}`, formattedItem);
                }
              }
            }
          });

          const finalUniqueArray = Array.from(uniqueBookingsMap.values()).sort((a, b) => b.id - a.id);
          setBookings(finalUniqueArray);

          if (finalUniqueArray.length > 0) {
            autoSyncBookings(finalUniqueArray);
          }
        }
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };
    fetchOtaBookings();
  }, [hotelCode, propertyId]);

  const filteredBookings = useMemo(() => {
    return bookings.filter((item) => {
      const resDetails = item.payload?.HotelReservations?.HotelReservation;
      const customer = resDetails?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
      const customerName = `${customer?.PersonName?.GivenName || ""} ${customer?.PersonName?.Surname || ""}`.trim();
      const channelName = item.payload?.POS?.Source?.BookingChannel?.CompanyName || "";
      const otaId = resDetails?.UniqueID?.ID || "";
      return `${customerName} ${channelName} ${otaId}`.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [bookings, searchText]);

  const currentRows = useMemo(() => {
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    return filteredBookings.slice(indexOfFirstRow, indexOfLastRow);
  }, [filteredBookings, currentPage, rowsPerPage]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}></div>
          <h6 className="fw-bold" style={{ color: NAVY, letterSpacing: '0.5px' }}>Loading Dashboard...</h6>
        </div>
      </div>
    );
  }

  const SUMMARY_CARDS = [
    { title: "Today's Bookings", value: summary.today, icon: "fa-calendar-check", link: "#", accent: GOLD },
    { title: "Tomorrow's Bookings", value: summary.tomorrow, icon: "fa-calendar-plus", link: "#", accent: "#3b82f6" },
    { title: "This Month", value: summary.month, icon: "fa-chart-line", link: "#", accent: "#10b981" },
    { title: "Future Bookings", value: summary.future, icon: "fa-forward", link: "#", accent: "#8b5cf6" },
    { title: "Live Booked Rooms", value: summary.live, icon: "fa-bed", link: "/live-reservations", accent: "#ef4444" },
    { title: "Available Rooms", value: summary.available, icon: "fa-door-open", link: "/available-rooms", accent: "#10b981" },
    { title: "Cleaning Status", value: summary.cleaning, icon: "fa-broom", link: "/cleaning-rooms", accent: "#64748b" },
    { title: "Total Inventory", value: summary.totalRooms, icon: "fa-hotel", link: "/rooms", accent: GOLD },
    { title: "WA Msg (This Month)", value: summary.whatsappCurrentMonth, icon: "fab fa-whatsapp", link: "#", accent: "#25D366" },
    { title: "WA Msg (Last Month)", value: summary.whatsappPrevMonth, icon: "fab fa-whatsapp", link: "#", accent: "#128C7E" }
  ];

  return (
    <div style={{ background: "#f4f6fa", marginBottom: 150 }}>
      <Menu />
      <div className="content-wrapper" style={{ padding: "14px 20px", background: "#f4f6fa" }}>
        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-4 g-3 mb-4">
          {SUMMARY_CARDS.map((c, i) => (
            <div className="col" key={i}>
              <div
                onClick={() => navigate(c.link)}
                style={{
                  position: "relative",
                  borderRadius: 16,
                  padding: "20px",
                  color: "#fff",
                  background: GRADIENT_PRIMARY,
                  cursor: "pointer",
                  minHeight: 105,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 6px 16px rgba(10, 37, 64, 0.08)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  overflow: "hidden",
                  marginBottom: 4
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 10px 22px rgba(10, 37, 64, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(10, 37, 64, 0.08)";
                }}
              >
                <i className={`fas ${c.icon}`} style={{ position: "absolute", top: 18, right: 18, fontSize: 24, color: c.accent, opacity: 0.8 }} />
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, textTransform: "uppercase", margin: 0, letterSpacing: '0.5px' }}>{c.title}</p>
                  <h2 className="mt-1 mb-0" style={{ fontSize: "1.85rem", fontWeight: 800 }}>{c.value}</h2>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="m-0 fw-bold" style={{ color: NAVY, fontSize: '1.05rem' }}>
              Real-time Integration Inbound Activity
            </h5>
          </div>

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Log ID</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Channel</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>OTA Ref ID</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Customer</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Check-in</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Check-out</TableCell>
                  <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Room Category</TableCell>
                  <TableCell align="right" sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Total Amt</TableCell>
                  <TableCell align="center" sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.75rem" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                      No active or upcoming bookings found for this property.
                    </TableCell>
                  </TableRow>
                ) : (
                  currentRows.map((row) => {
                    const resReservation = row.payload?.HotelReservations?.HotelReservation;
                    const customer = resReservation?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
                    const roomStay = resReservation?.RoomStays?.[0];

                    const otaId = resReservation?.UniqueID?.ID || "N/A";
                    const channel = row.payload?.POS?.Source?.BookingChannel?.CompanyName;
                    const fullName = `${customer?.PersonName?.GivenName || ""} ${customer?.PersonName?.Surname || ""}`.trim() || "Guest";
                    const checkIn = roomStay?.TimeSpan?.Start || "N/A";
                    const checkOut = roomStay?.TimeSpan?.End || "N/A";
                    const roomName = roomStay?.RoomTypes?.RoomType?.RoomDescription?.Name || "N/A";
                    const totalAmt = roomStay?.Total?.AmountBeforeTax || roomStay?.Total?.AmountAfterTax || "0";
                    const currency = roomStay?.Total?.CurrencyCode || "INR";

                    return (
                      <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: row.isTransferred ? '#f8fafc' : 'inherit' }}>
                        <TableCell sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{row.id}</TableCell>
                        <TableCell>{getChannelChip(channel)}</TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>{otaId}</TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>{fullName}</TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", color: "#059669", fontWeight: 500 }}>{checkIn}</TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", color: "#dc2626", fontWeight: 500 }}>{checkOut}</TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", color: "#475569" }}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <HotelOutlined sx={{ fontSize: 14, color: "#64748b" }} />
                            {roomName}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#1e293b" }}>
                          {currency} {totalAmt}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" justifyContent="center" gap={1}>
                            <IconButton size="small" onClick={() => handleViewDetails(row)} sx={{ color: "#3b82f6" }}>
                              <Visibility sx={{ fontSize: 18 }} />
                            </IconButton>

                            <Button
                              variant={row.isTransferred ? "outlined" : "contained"}
                              size="small"
                              disabled={row.isTransferred}
                              startIcon={row.isTransferred ? <CheckCircleOutline sx={{ fontSize: 14 }} /> : <AssignmentTurnedIn sx={{ fontSize: 14 }} />}
                              onClick={() => !row.isTransferred && processReservationTransfer(row, false)}
                              sx={{
                                fontSize: "0.7rem",
                                textTransform: "none",
                                padding: "2px 8px",
                                borderRadius: "6px",
                                backgroundColor: row.isTransferred ? "transparent" : "#10b981",
                                color: row.isTransferred ? "#64748b" : "#fff",
                                borderColor: row.isTransferred ? "#cbd5e1" : "transparent",
                                '&:hover': { backgroundColor: row.isTransferred ? "transparent" : "#059669" }
                              }}
                            >
                              {row.isTransferred ? "Transferred" : "Transfer"}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-8">
            <Card style={{ padding: 24, borderRadius: 16, border: "none", background: "#ffffff", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", height: "100%" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="m-0 fw-bold" style={{ color: NAVY }}>Weekly Activity Matrix</h6>
                  <p className="text-muted m-0" style={{ fontSize: 11 }}>Calculated booking trends over the past 7 days</p>
                </div>
              </div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dynamicBarData} margin={{ top: 10, right: 5, left: -25, bottom: 0 }}>
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }} dy={8} />
                    <YAxis axisLine={false} tickLine={false} hide />
                    <Tooltip cursor={{ fill: "#f1f5f9", radius: 8 }} contentStyle={{ borderRadius: "10px", border: "none", boxShadow: "0 8px 20px rgba(0,0,0,0.06)" }} />
                    <Bar dataKey="bookings" fill="#1e3a8a" radius={[6, 6, 2, 2]} barSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="col-lg-4">
            <Card style={{ padding: 24, borderRadius: 16, border: "none", background: "#ffffff", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div>
                <h6 className="m-0 fw-bold" style={{ color: NAVY }}>Inventory Segmentation</h6>
                <p className="text-muted m-0" style={{ fontSize: 11 }}>Real-time spatial distribution</p>
              </div>
              <div style={{ position: "relative", width: "100%", height: 160 }} className="my-2">
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", zIndex: 1 }}>
                  <h3 className="m-0 fw-bold" style={{ color: NAVY }}>{summary.totalRooms}</h3>
                  <span className="text-muted fw-bold" style={{ fontSize: 9 }}>UNITS</span>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dynamicPieData} dataKey="value" innerRadius={52} outerRadius={70} paddingAngle={4} stroke="none">
                      {dynamicPieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="d-flex justify-content-center gap-3 bg-light p-2 rounded-3" style={{ fontSize: 11 }}>
                {dynamicPieData.map((d, i) => (
                  <div key={i} className="d-flex align-items-center">
                    <span className="rounded-circle me-1" style={{ width: 6, height: 6, backgroundColor: d.color }} />
                    <span className="fw-bold text-dark">{d.value} <span className="text-muted fw-normal">{d.name}</span></span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>

        <h5 className="mb-3 fw-bold" style={{ color: NAVY, fontSize: '1.05rem' }}>
          <i className="fas fa-folder-open text-muted me-2" style={{ fontSize: 13 }} /> System Core Document Registers
        </h5>
        <div className="row row-cols-2 row-cols-md-3 row-cols-lg-6 g-3">
          {REPORTS.map((r, i) => (
            <div className="col" key={i}>
              <div
                onClick={() => navigate(r.link)}
                style={{
                  background: "#ffffff",
                  padding: "16px",
                  borderRadius: 12,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
                  height: "100%",
                  cursor: "pointer",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.borderColor = NAVY_LIGHT;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <i className="fas fa-file-invoice text-primary" style={{ fontSize: 15 }} />
                  <i className="fas fa-chevron-right text-muted opacity-50" style={{ fontSize: 9 }} />
                </div>
                <p className="fw-bold text-dark mb-0" style={{ fontSize: 12, lineHeight: '1.3' }}>{r.title}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

export default Dashboard;